import React, { useEffect, useRef, useState } from "react";
import { X, Eye, Upload } from "lucide-react";
import {
  getInsuranceDocuments,
  uploadInsuranceCard,
  deletePatientDocument,
  viewPatientDocument,
} from "../APIs/patientDocumentApi";

const SLOTS = [
  { key: "INSURANCE_CARD_FRONT", label: "Front" },
  { key: "INSURANCE_CARD_BACK", label: "Back" },
];

function DropZone({ label, existing, onFile, onDelete, onView, viewing }) {
  const inputRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  };

  return (
    <div>
      <p className="text-sm font-semibold text-gray-700 mb-2">{label}</p>
      {existing ? (
        <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-gray-50">
          <span className="text-sm text-gray-700 truncate flex-1">
            {existing.fileName}
          </span>
          <button
            onClick={onView}
            disabled={viewing}
            className="text-gray-400 hover:text-teal-600 transition disabled:opacity-40"
            title="View"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="text-gray-400 hover:text-red-500 transition"
            title="Remove"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current.click()}
          className="border-2 border-dashed border-gray-300 rounded-lg px-4 py-6 flex flex-col items-center gap-1 cursor-pointer hover:border-teal-400 transition"
        >
          <Upload className="w-5 h-5 text-gray-400" />
          <p className="text-sm text-gray-500">
            Drag & drop or click to upload
          </p>
          <p className="text-xs text-gray-400">PDF, JPG, PNG · max 5 MB</p>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => {
              if (e.target.files[0]) onFile(e.target.files[0]);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function InsuranceCardModal({
  patientId,
  insurance,
  onClose,
  onSaved,
}) {
  const [existing, setExisting] = useState({
    INSURANCE_CARD_FRONT: null,
    INSURANCE_CARD_BACK: null,
  });
  const [queued, setQueued] = useState({
    INSURANCE_CARD_FRONT: null,
    INSURANCE_CARD_BACK: null,
  });
  const [toDelete, setToDelete] = useState({
    INSURANCE_CARD_FRONT: null,
    INSURANCE_CARD_BACK: null,
  });
  const [viewing, setViewing] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getInsuranceDocuments(insurance.id)
      .then((docs) => {
        const map = { INSURANCE_CARD_FRONT: null, INSURANCE_CARD_BACK: null };
        docs.forEach((d) => {
          if (map.hasOwnProperty(d.category)) map[d.category] = d;
        });
        setExisting(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [insurance.id]);

  const handleFile = (category, file) => {
    setQueued((q) => ({ ...q, [category]: file }));
  };

  const handleDelete = (category) => {
    setToDelete((d) => ({ ...d, [category]: existing[category] }));
    setExisting((e) => ({ ...e, [category]: null }));
  };

  const handleView = async (doc) => {
    setViewing((v) => ({ ...v, [doc.documentId]: true }));
    try {
      const url = await viewPatientDocument(doc.documentId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      alert("Unable to load document. Please try again.");
    } finally {
      setViewing((v) => ({ ...v, [doc.documentId]: false }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const category of Object.keys(toDelete)) {
        const doc = toDelete[category];
        if (doc) await deletePatientDocument(doc.documentId, patientId);
      }
      for (const category of Object.keys(queued)) {
        const file = queued[category];
        if (file)
          await uploadInsuranceCard(patientId, insurance.id, file, category);
      }
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Insurance Card</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-4">Loading...</p>
          ) : (
            SLOTS.map(({ key, label }) => (
              <DropZone
                key={key}
                label={label}
                existing={
                  queued[key] ? { fileName: queued[key].name } : existing[key]
                }
                onFile={(f) => handleFile(key, f)}
                onDelete={() => {
                  if (queued[key]) {
                    setQueued((q) => ({ ...q, [key]: null }));
                  } else {
                    handleDelete(key);
                  }
                }}
                onView={() => handleView(existing[key])}
                viewing={
                  existing[key] ? viewing[existing[key].documentId] : false
                }
              />
            ))
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
